import BaseOptionChart from 'common/components/charts/BaseOptionChart'
import { createArrayOfNumber } from 'common/utils/array'
import { dynamicTickAmount, integerFormatter } from 'common/utils/chart'
import { fNumber, formatNumber } from 'common/utils/formatNumber'
import merge from 'lodash/merge'
import ReactApexChart from 'react-apexcharts'
import { useTranslation } from 'react-i18next'

import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'
import { useGetXTitleLineChart } from 'modules/pond/api/selector'
import { useShrimpEventAnnotations } from 'hooks/useShrimpEventAnnotations'
import { useGetPartials } from 'modules/food/api/queries'

interface LineChartProps {
  yData: ApexAxisChartSeries
  isLoading: boolean
  dataLength: number
  options?: ApexCharts.ApexOptions
  min?: number
  max?: number
  yTitle?: string
}

const LineChart = ({ yData, isLoading, dataLength, options, min, max, yTitle }: LineChartProps) => {
  const { t } = useTranslation('water')
  const theme = useTheme()
  const xTitle = useGetXTitleLineChart()
  const { data: partials } = useGetPartials()
  const eventAnnotations = useShrimpEventAnnotations(partials)

  const baseYaxisOption = {
    title: {
      text: yTitle || t('value'),
    },
    labels: {
      formatter: formatNumber,
    },
    ...(min !== undefined && { min }),
    ...(max !== undefined && { max }),
  }

  const yTooltipFormatter = (value: string | number) => {
    if (typeof value !== 'undefined') return `${fNumber(value)}`
    return value
  }
  const chartOptions = merge(BaseOptionChart({ showPartialAnnotation: true }), {
    xaxis: {
      type: 'numeric',
      title: {
        text: xTitle,
      },
      tickAmount: dynamicTickAmount(dataLength),
      labels: {
        formatter: integerFormatter,
      },
    },
    yaxis: baseYaxisOption,
    tooltip: {
      x: {
        show: true,
        formatter: (v: number) => {
          return `${t('shrimp.age')}: ${v} ${t('day')}`
        },
      },
      marker: { show: false },
      y: { formatter: yTooltipFormatter },
    },
    annotations: {
      xaxis: eventAnnotations,
    },
    responsive: [
      {
        breakpoint: theme.breakpoints.values.sm,
        options: {
          legend: {
            position: 'bottom',
          },
          yaxis: {
            title: {
              text: undefined,
            },
            labels: {
              ...baseYaxisOption.labels,
              offsetX: -10,
            },
          },
          xaxis: {
            title: {
              offsetY: -20,
            },
          },
        },
      },
    ],
    ...options,
  })

  if (isLoading)
    return (
      <Stack spacing={3} p={2}>
        <Stack direction="row" justifyContent="flex-start" spacing={1}>
          {createArrayOfNumber(3).map((i) => (
            <Skeleton width={60} key={i} />
          ))}
        </Stack>
        {createArrayOfNumber(6).map((i) => (
          <Stack direction="row" key={i} spacing={1}>
            <Skeleton width={40} />
            <Skeleton width="100%" />
          </Stack>
        ))}
        <Stack direction="row" spacing={2}>
          {createArrayOfNumber(6).map((i) => (
            <Skeleton key={i} width="100%" />
          ))}
        </Stack>
      </Stack>
    )

  return <ReactApexChart type="line" series={yData} options={chartOptions} height={420} />
}

export default LineChart
